import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import SuicideIntentForm from "./SuicideIntentForm";
import { useDispatch } from "react-redux";
import { createSuicideIntentAssessment } from "../../../store/slices/suicideIntentSlice";
import { fetchPatient } from "../../../store/slices/patientsSlice"; // Added this import

const AddSuicideIntent = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId,
        patientId,
        patientName: "",
        dateAssessed: new Date().toISOString().split("T")[0],
        clientName: "",
        diagnosis: "",
        assessedBy: "",
        isolation: "",
        timing: "",
        precautions: "",
        actingToGetHelp: "",
        finalActs: "",
        activePreparation: "",
        suicideNote: "",
        overtCommunication: "",
        allegedPurpose: "",
        expectationsFatality: "",
        conceptionLethality: "",
        seriousness: "",
        attitudeLivingDying: "",
        conceptionRescuability: "",
        premeditation: "",
        reactionAttempt: "",
        visualizationDeath: "",
        previousAttempts: "",
        intakeAlcohol: "",
        intakeDrug: "",
        additionalComments: "",
        clinicianName: "",
        signature: "",
        dateSigned: ""
    });

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient for Suicide Intent:", err));
        }
    }, [dispatch, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const calculateTotal = () => {
        const fields = ["isolation", "timing", "precautions", "actingToGetHelp", "finalActs", "activePreparation", "suicideNote", "overtCommunication", "allegedPurpose", "expectationsFatality", "conceptionLethality", "seriousness", "attitudeLivingDying", "conceptionRescuability", "premeditation", "reactionAttempt", "visualizationDeath", "previousAttempts", "intakeAlcohol", "intakeDrug"];
        return fields.reduce((sum, field) => sum + (parseInt(formData[field] || 0, 10)), 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const toSnakeCase = (str) => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

        // Map each item to score and value
        const questionPayload = {};
        const itemsWithOptions = [
            { id: "isolation", options: ["Somebody present", "Somebody nearby, or in visual or vocal contact", "No one nearby or in visual or vocal contact"] },
            { id: "timing", options: ["Intervention is probable", "Intervention is not likely", "Intervention is highly unlikely"] },
            { id: "precautions", options: ["No precautions", "Passive precautions (avoiding others but doing nothing to prevent intervention; alone in room with unlocked door)", "Active precautions (locked door)"] },
            { id: "actingToGetHelp", options: ["Notified potential helper regarding attempt", "Contacted but did not specifically notify potential helper regarding attempt", "Did not contact or notify potential helper"] },
            { id: "finalActs", options: ["None", "Thought about or made some arrangements", "Made definite plans or completed arrangements"] },
            { id: "activePreparation", options: ["None", "Minimal to moderate", "Extensive"] },
            { id: "suicideNote", options: ["Absence of note", "Note written, but torn up; note thought about", "Presence of note"] },
            { id: "overtCommunication", options: ["None", "Equivocal communication", "Unequivocal communication"] },
            { id: "allegedPurpose", options: ["To manipulate environment, get attention, revenge", "Components of '0' and '2'", "To escape, surcease, solve problems"] },
            { id: "expectationsFatality", options: ["Thought that death was unlikely", "Thought that death was possible but not probable", "Thought that death was probable or certain"] },
            { id: "conceptionLethality", options: ["Did less to self than he thought would be lethal", "Wasn't sure if what he did would be lethal", "Equaled or exceeded what he thought would be lethal"] },
            { id: "seriousness", options: ["Did not seriously attempt to end life", "Uncertain about seriousness to end life", "Seriously attempted to end life"] },
            { id: "attitudeLivingDying", options: ["Did not want to die", "Components of '0' and '2'", "Wanted to die"] },
            { id: "conceptionRescuability", options: ["Thought that death would be unlikely if he received medical attention", "Was uncertain whether death could be averted by medical attention", "Was certain of death even if he received medical attention"] },
            { id: "premeditation", options: ["None; Impulsive", "Suicide contemplated for three hours or less prior to attempt", "Suicide contemplated for more than three hours prior to attempt"] },
            { id: "reactionAttempt", options: ["Sorry about attempt; feels foolish, ashamed", "Accepts both attempt and its failure", "Regrets failure of attempt"] },
            { id: "visualizationDeath", options: ["Life-after-death, reunion with decedents", "Never-ending sleep, darkness, end-of-things", "No conceptions of, or thoughts about death"] },
            { id: "previousAttempts", options: ["None", "One or two", "Three or more"] },
            { id: "intakeAlcohol", options: ["Some alcohol intake prior to but not related to attempt, reportedly not enough to impair judgment", "Enough alcohol intake to impair judgment, reality testing and diminish responsibility/impulse control", "Intentional intake of alcohol in order to facilitate implementation of suicide attempt"] },
            { id: "alcoholIntakeAlcohol", title: "Relationship between Alcohol Intake and Attempt", options: ["Some alcohol intake prior to but not related to attempt, reportedly not enough to impair judgment", "Enough alcohol intake to impair judgment, reality testing and diminish responsibility/impulse control", "Intentional intake of alcohol in order to facilitate implementation of suicide attempt"] },
            { id: "intakeDrug", options: ["Some drug intake prior to but not related to attempt, reportedly not enough to impair judgment", "Enough drug intake to impair judgment, identity testing and diminish responsibility/impulse control", "Intentional drug intake in order to facilitate implementation of suicide attempt"] },
        ];

        itemsWithOptions.forEach((item) => {
            const score = parseInt(formData[item.id] || 0, 10);
            const value = item.options[score] || "";
            questionPayload[`${toSnakeCase(item.id)}_score`] = score;
            questionPayload[`${toSnakeCase(item.id)}_value`] = value;
        });

        // Calculate total score
        const total_score = Object.keys(questionPayload)
            .filter(k => k.endsWith("_score"))
            .reduce((sum, k) => sum + questionPayload[k], 0);

        // Determine risk
        const risk_level = total_score <= 10 ? "Low Risk" : total_score <= 19 ? "Medium Risk" : "High Risk";

        const payload = {
            patient_id: formData.patientId || null,
            case_id: formData.caseId || null,
            client_name: formData.clientName || "",
            diagnosis: formData.diagnosis || "",
            assessed_by: formData.assessedBy || "",
            ...questionPayload,
            total_score,
            risk_level,
            additional_comments: formData.additionalComments,
            clinician_name: formData.clinicianName,
            status: "active",
        };

        try {
            const res = await dispatch(createSuicideIntentAssessment(payload)).unwrap();

            Swal.fire({
                title: "Saved!",
                text: `Suicide Intent Scale completed. Total Score: ${total_score} → ${risk_level}`,
                icon: "success",
                timer: 2500,
                showConfirmButton: false
            });

            navigate(`/phase04/suicide-intent/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (error) {
            Swal.fire("Error", error || "Failed to save Suicide Intent Scale.", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <SuicideIntentForm formData={formData} handleChange={handleChange} />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary sticky-form-submit">
                                        <i className="ri-save-2-line me-1"></i> Save
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddSuicideIntent;